import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { fetchAcquisitionSummary } from "../../services/acquisitionApi";
import { useAppSelector } from "../../store/hook";
import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

export interface AcquisitionSummaryData {
  data: Data;
  leads: Lead[];
  aquisitions: Aquisition[];
  status: Status;
  validInquiries: number;
}

export interface Data {
  totalConversion: number;
  totalAcquisition: string;
  totalLost: number;
}

export interface Lead {
  month: string;
  year: string;
  source: Source[];
}

export interface Source {
  count: any;
  channel: string;
}

export interface Aquisition {
  month: string;
  year: string;
  source: Source2[];
}

export interface Source2 {
  count: string;
  channel: string;
}

export interface Status {
  error: boolean;
  msg: string;
}

interface Props {
  isPrintMode: boolean;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
  clinicData: string;
}

const AcquisitionSection: React.FC<Props> = ({
  isPrintMode,
  dateFilter,
  clinicData,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AcquisitionSummaryData | null>(null);

  const fetchChart = async () => {
    setLoading(true);
    const request = {
      clinicUniqueId: clinicData,
      startDate: dateFilter.startDate,
      endDate: dateFilter.endDate,
    };
    try {
      const response = await fetchAcquisitionSummary(request);

      if (response?.status?.msg === "Success") {
        setData(response);
      }
    } catch (error) {
      console.error("Error fetching acquisition data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clinicData && dateFilter?.startDate && dateFilter?.endDate) {
      fetchChart();
    }
  }, [dateFilter, clinicData]);

  const LoadingSpinner = styled(CircularProgress)({
    color: "#194E5B",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  });

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md h-[600px] relative">
        <h2 className="text-2xl font-bold text-[#194e5b] mb-4">
          Acquisitions Summary
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="col-span-2">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  console.log(data, "response123456");

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        No data available
      </div>
    );
  }

  const columns: GridColDef[] = [
    {
      field: "channel",
      headerName: "Source",
      flex: 1,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
    },
    {
      field: "count",
      headerName: "Count",
      width: 90,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
    },
  ];

  const pieData = Object.entries(data?.leads || {}).map(([channel, count]) => ({
    name: channel,
    value: Number(count),
  }));

  console.log(pieData, "pieData");

  // In a real implementation, you would:
  // 1. Fetch data from an API that includes dates
  // 2. Filter that data based on the date range
  // 3. Update the state with the filtered data
  // For now, we'll just use the existing data

  const getConversionBenchmark = (rate: number) => {
    if (rate >= 75) {
      return {
        status: "excellent",
        message: "Excellent conversion rate! Keep up the great work.",
        color: "#4dccc3",
      };
    } else if (rate >= 60) {
      return {
        status: "improvement",
        message:
          "Need to improve conversions by improving processes for follow-up and speed to lead.",
        color: "#f3bb4d",
      };
    } else if (rate >= 50) {
      return {
        status: "warning",
        message:
          "WARNING: Your listing on the rewards portal will be lowered due to poor conversion rates.",
        color: "#e05a5a",
      };
    } else {
      return {
        status: "critical",
        message:
          "RED FLAG: Critical conversion rate. This may result in disabling your listing.",
        color: "#d32f2f",
      };
    }
  };

  const rate =
    data?.validInquiries && data?.data?.totalAcquisition
      ? (
          (Number(data.data.totalAcquisition || 1) /
            Number(data.validInquiries)) *
          100
        ).toFixed()
      : "0.00";

  const conversionBenchmark = getConversionBenchmark(Number(rate));
  return (
    <div className="bg-white p-6 rounded-lg shadow-md mt-4">
      <h2 className="text-2xl font-bold text-[#194e5b] mb-4">
        Acquisitions Summary
      </h2>

      <div
        style={{
          backgroundColor: "#ffffff",
          border: `2px solid ${conversionBenchmark.color}`,
          borderRadius: "8px",
          padding: "1rem",
          marginBottom: "2rem",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <h3
          style={{
            color: conversionBenchmark.color,
            marginBottom: "0.5rem",
            fontSize: "1.1rem",
          }}
        >
          Conversion Benchmark
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <span style={{ fontSize: "0.9rem", color: "#666" }}>
            Valid Inquiries: <strong>{data?.validInquiries}</strong>
          </span>
          <span style={{ fontSize: "0.9rem", color: "#666" }}>
            Conversions: <strong>{data?.data?.totalAcquisition}</strong>
          </span>
          <span
            style={{
              fontSize: "1rem",
              fontWeight: "bold",
              color: conversionBenchmark.color,
            }}
          >
            Rate: {Number(rate)?.toFixed(2)}%
          </span>
        </div>
        <div
          style={{ fontSize: "0.75rem", color: "#888", marginBottom: "0.5rem" }}
        >
          * Excludes Patient Callbacks, No Show/Cancelled/Removed from
          Acquisition, and Removed/Duplicate entries
        </div>
        <p
          style={{
            color: conversionBenchmark.color,
            fontWeight: "bold",
            margin: "0.5rem 0 0 0",
            fontSize: "0.9rem",
          }}
        >
          {conversionBenchmark.message}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#f5f7fa] p-4 rounded-lg shadow-sm flex flex-col items-center justify-center">
          <div className="text-sm font-semibold mb-2">Total Leads</div>
          <div className="text-2xl font-bold text-gray-800">
            {data?.data?.totalConversion}
          </div>
        </div>
        <div className="bg-[#f5f7fa] p-4 rounded-lg shadow-sm flex flex-col items-center justify-center">
          <div className="text-sm font-semibold mb-2">
            New Patients Converted
          </div>
          <div className="text-2xl font-bold text-[#00c49f]">
            {data?.data?.totalAcquisition}
          </div>
        </div>
        <div className="bg-[#f5f7fa] p-4 rounded-lg shadow-sm flex flex-col items-center justify-center">
          <div className="text-sm font-semibold mb-2">Lost</div>
          <div className="text-2xl font-bold text-[#ff8042]">
            {data?.data?.totalLost}
          </div>
        </div>
      </div>

      {/* Acquisition Sources and Details */}
      <div className="h-[100%] grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Pie Chart */}
        <div className="h-[100%] flex flex-col">
          <h3 className="text-xl font-semibold text-[#194e5b] mb-4">
            Lead Sources
          </h3>
          <div className="flex-grow">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={pieData?.length === 1 ? 0 : 60}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent, value }) => {
                    if (value === 0) return null; // Don't show label if value is zero
                    if (pieData?.length === 1) {
                      return `${name}\n${(percent * 100).toFixed(0)}%`;
                    }
                    return `${name} ${(percent * 100).toFixed(0)}%`;
                  }}
                  labelLine={false}
                  startAngle={90}
                  endAngle={pieData?.length === 1 ? -270 : 450}
                >
                  {pieData?.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        [
                          "#FF6384",
                          "#36A2EB",
                          "#FFCE56",
                          "#4BC0C0",
                          "#9966FF",
                          "#FF9F40",
                          "#8DD17E",
                          "#D3A4FF",
                          "#FF6B6B",
                          "#6B8E23",
                        ][index % 10]
                      }
                      style={entry.value === 0 ? { opacity: 0.3 } : {}} // Optional: make zero-value segments semi-transparent
                    />
                  ))}
                  {/* <Label
                    value={`Total: ${pieData?.reduce(
                      (acc, curr) => acc + curr.value,
                      0
                    )}`}
                    position="center"
                    style={{
                      fill: "#194E5B",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  /> */}
                </Pie>
                <Tooltip />

                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  formatter={(value, entry, index) => {
                    // Don't show legend items for zero values
                    return (
                      <span style={{ color: "#194E5B", fontWeight: 500 }}>
                        {value}
                      </span>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right: DataGrid */}
        <div className="h-[100%] flex flex-col">
          <h3 className="text-xl font-semibold text-[#194e5b] mb-4">
            Acquisition Details
          </h3>
          <div className="flex-grow overflow-hidden">
            <DataGrid
              rows={Object.entries(data?.aquisitions || {}).map(
                ([channel, count], index) => ({
                  id: index + 1,
                  channel,
                  count: Number(count),
                })
              )}
              columns={columns}
              autoHeight={false}
              hideFooter
              disableRowSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  border: "none",
                },
                "& .MuiDataGrid-columnHeaders": {
                  border: "none",
                  backgroundColor: "#F5F7FA",
                  color: "#194E5B",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcquisitionSection;
